// Stage Mail-V2 (2026-07-22 / Step 15): useBatchActions — bulk 真实 后端 调用。
//
// 设计:
//   - run(messageIds, action, payload?)
//   - 完成后 onSettled(updated, skipped, errors) 回调
//   - 失败 时 (整个 batch 5xx) → onError(msg)
//   - in-flight 防双击
//
// MailV2Page 接 onSettled 后:
//   - onSettled.ok=true → refresh() messages + folderCounts
//   - onSettled.ok=false → 保留 selection 状态

import { useCallback, useRef, useState } from "react";
import { mailV2Api } from "../../../api/mail-v2";

export type BatchActionKind =
  | "mark_read"
  | "mark_unread"
  | "star"
  | "unstar"
  | "archive"
  | "trash"
  | "spam"
  | "unspam"
  | "restore_to_inbox"
  | "move"
  | "add_label"
  | "remove_label";

export interface BatchActionPayload {
  target_mailbox?: string;
  label_id?: string;
}

export interface BatchActionResult {
  updated: number;
  skipped: number;
  errors: { message_id: string; error_code: string; message: string }[];
  total: number;
}

export interface UseBatchActionsOptions {
  enabled: boolean;
  onSettled?: (result: BatchActionResult) => void;
  onError?: (msg: string) => void;
}

export function useBatchActions(opts: UseBatchActionsOptions) {
  const { enabled } = opts;
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<BatchActionResult | null>(null);
  const inFlightRef = useRef<boolean>(false);

  const run = useCallback(
    async (
      messageIds: string[],
      action: BatchActionKind,
      payload?: BatchActionPayload,
    ): Promise<BatchActionResult | null> => {
      if (!enabled) return null;
      if (inFlightRef.current) return null;
      if (!messageIds || messageIds.length === 0) return null;
      inFlightRef.current = true;
      setPending(true);
      setError(null);
      try {
        const result = await mailV2Api.batchActions(
          messageIds,
          action,
          payload,
        );
        setLastResult(result);
        opts.onSettled?.(result);
        return result;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        opts.onError?.(msg);
        return null;
      } finally {
        inFlightRef.current = false;
        setPending(false);
      }
    },
    [enabled, opts],
  );

  return { run, pending, error, lastResult };
}
